import { ComponentProps, FunctionComponent, ReactNode, useMemo } from 'react';

import {
	CalendarGreenCheck,
	CalendarRedX,
	LeadGreen,
	LeadRed,
	MessageBubbles,
	MessageBubblesGreenCheck,
	MessageBubblesRedX
} from '@/resources/assets/images';
import type { BusinessQuery } from '@/modules/dashboard/dashboardTypes';
import { sendAnalytics } from '@/services/analyticsService';
import { Input } from '@/components/atoms/Input';
import { FilterComponents } from '../../molecules/FilterComponents';
import type { FilterProps } from '../../../@types';

export const FilterContactStatus = ({
	appliedFilters,
	onFilterChange
}: FilterProps) => {
	const filtersCount = useMemo(() => {
		const countGroups = [
			['contactedYes', 'contactedNo'],
			['scheduledYes', 'scheduledNo']
		];

		return countGroups.reduce(
			(count, group) => {
				if (
					group.some(
						key => appliedFilters?.[key as keyof BusinessQuery]
					)
				) {
					return count + 1;
				}
				return count;
			},
			appliedFilters?.lead !== undefined ? 1 : 0
		);
	}, [appliedFilters]);

	const handleChangeContactStatusFilter = ({
		name,
		value
	}: ChangeFiltersProps) => {
		const newQuery = {
			...appliedFilters,
			contactedYes:
				name === 'isContacted'
					? value === 'true'
					: (appliedFilters?.contactedYes ?? undefined),
			contactedNo:
				name === 'isContacted'
					? value === 'false'
					: (appliedFilters?.contactedNo ?? undefined),
			scheduledYes:
				name === 'isScheduled'
					? value === 'true'
					: (appliedFilters?.scheduledYes ?? undefined),
			scheduledNo:
				name === 'isScheduled'
					? value === 'false'
					: (appliedFilters?.scheduledNo ?? undefined),
			lead:
				name === 'isLead'
					? value === 'true'
					: (appliedFilters?.lead ?? undefined)
		};

		sendAnalytics({
			type: 'track',
			eventName: 'User changed the contact status filters',
			properties: {
				appliedFilters: newQuery
			}
		});

		onFilterChange({
			skip: 0,
			limit: 50,
			...newQuery
		});
	};

	const handleClearContactFilters = () => {
		sendAnalytics({
			type: 'track',
			eventName: 'User cleared all contact status filters',
			properties: {
				appliedFilters
			}
		});

		const queryNew = {
			...appliedFilters,
			contactedYes: undefined,
			contactedNo: undefined,
			scheduledYes: undefined,
			scheduledNo: undefined,
			lead: undefined
		};

		onFilterChange({
			skip: 0,
			limit: 50,
			...queryNew
		});
	};

	return (
		<FilterComponents.Wrapper moduleName="contact-status">
			<FilterComponents.Header
				icon={MessageBubbles}
				title="Contact Status"
				showClearFiltersButton={filtersCount > 0}
				activeFiltersCount={filtersCount}
				onClearFilters={handleClearContactFilters}
			/>

			<FilterComponents.Content>
				<StatusControl title="Contact status">
					<StatusOption
						icon={MessageBubblesGreenCheck}
						label="Contacted"
						value="true"
						name="isContacted"
						checked={appliedFilters?.contactedYes || false}
						onChange={handleChangeContactStatusFilter}
					/>
					<StatusOption
						icon={MessageBubblesRedX}
						label="Not Contacted"
						value="false"
						name="isContacted"
						checked={appliedFilters?.contactedNo || false}
						onChange={handleChangeContactStatusFilter}
					/>
				</StatusControl>

				<StatusControl title="Scheduled Status">
					<StatusOption
						icon={CalendarGreenCheck}
						label="Scheduled"
						value="true"
						name="isScheduled"
						checked={appliedFilters?.scheduledYes || false}
						onChange={handleChangeContactStatusFilter}
					/>
					<StatusOption
						icon={CalendarRedX}
						label="Not Scheduled"
						value="false"
						name="isScheduled"
						checked={appliedFilters?.scheduledNo || false}
						onChange={handleChangeContactStatusFilter}
					/>
				</StatusControl>

				<StatusControl title="Lead Status">
					<StatusOption
						icon={LeadGreen}
						label="Lead"
						value="true"
						name="isLead"
						checked={appliedFilters?.lead === true}
						onChange={handleChangeContactStatusFilter}
					/>
					<StatusOption
						icon={LeadRed}
						label="Not Lead"
						value="false"
						name="isLead"
						checked={appliedFilters?.lead === false}
						onChange={handleChangeContactStatusFilter}
					/>
				</StatusControl>
			</FilterComponents.Content>
		</FilterComponents.Wrapper>
	);
};

type StatusControlProps = {
	title: string;
	children: ReactNode;
};

const StatusControl = ({ title, children }: StatusControlProps) => (
	<section className="flex flex-col gap-3 border-b border-dm-strong-mid-grey pb-4">
		<p className="text-sm text-dm-white">{title}</p>

		{children}
	</section>
);

type ChangeFiltersProps = {
	name: string;
	value: 'true' | 'false';
};

type StatusOptionProps = ChangeFiltersProps & {
	label: string;
	icon: FunctionComponent<ComponentProps<'svg'>>;
	onChange: ({ name, value }: ChangeFiltersProps) => void;
	checked: boolean;
};

const StatusOption = ({
	value,
	name,
	label,
	icon: Icon,
	checked,
	onChange
}: StatusOptionProps) => {
	const handleChangeOptionValue = () => {
		onChange({
			name,
			value
		});
	};

	return (
		<label className="flex cursor-pointer items-center gap-1.5">
			<Input
				type="radio"
				size="sm"
				name={name}
				value={value}
				onChange={handleChangeOptionValue}
				checked={checked}
			/>

			<span className="flex items-center gap-1">
				<Icon />
				<p>{label}</p>
			</span>
		</label>
	);
};
